import { Beaker, ChevronDown, ChevronUp } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
// Transport APIs are used in ChemicalTransport component
import {
  useDeleteChemicalData,
  usePostChemicalData,
  usePutChemicalData
} from "../../../api/chemical";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { Badge } from "../../../components/ui/common/badge";
import { Card, CardContent, CardHeader } from "../../../components/ui/common/card";
import { DEFAULT_CHEMICAL } from "../../../config/constants";
import { AccordionNames } from "../../../helpers/AccordionHelper";
import { errorView, successMessage } from "../../../helpers/ErrorHandler";
import ChemicalRecord from "./components/ChemicalRecord";
import ChemicalTransportPromptDialog from "./components/ChemicalTransportPromptDialog";
import { ChemicalSaveOutcome } from "./chemicalContent.types";
import { useChemicalTransportToggle } from "./hooks/useChemicalTransportToggle";

export type { ChemicalSaveOutcome };

export interface IChemicalRecord {
  _id?: string | number;
  chemicalName: string;
  chemicalType?: string | number;
  amount: string;
  isTransportationDetailsAvailable: boolean;
  transportationTypes: any[];
  unit?: string;
}

export interface IChemicalForm {
  chemicals: IChemicalRecord[];
}

export interface ITransportType {
  transportType: string;
  distance: number;
}

interface ChemicalContentProps {
  chemicalDataType: any;
  chemicalData: any;
  processId: string;
  TransportTypeList?: any[];
  refetchChemical: () => void;
  type: string;
  supplierDataHandle?: boolean;
  lockStatus?: boolean;
  handleOpenValidation?: () => boolean;
  currentAccordionStatus: (activeStatus: boolean, requestType: AccordionNames) => boolean;
  onChemicalSave?: (processId: string) => void;
}

export const ChemicalContent: React.FC<ChemicalContentProps> = ({
  chemicalDataType,
  chemicalData,
  processId,
  TransportTypeList = [],
  refetchChemical,
  type,
  supplierDataHandle = false,
  lockStatus = false,
  handleOpenValidation,
  currentAccordionStatus,
  onChemicalSave
}) => {

  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chemicalToDelete, setChemicalToDelete] = useState<{
    index: number;
    id?: string | number;
    name: string;
  } | null>(null);
  const [transportPrompt, setTransportPrompt] = useState<{
    index: number;
    recordId: string;
  } | null>(null);
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const expandedRecordIdRef = useRef<string | null>(null);
  const confirmingTransportRef = useRef(false);

  useEffect(() => {
    expandedRecordIdRef.current = expandedRecordId;
  }, [expandedRecordId]);

  // API Hooks - Chemical CRUD (Transport CRUD is in ChemicalTransport component)
  const { mutate: postChemicalData } = usePostChemicalData();
  const { mutate: putChemicalData } = usePutChemicalData();
  const { mutate: deleteChemicalDataMutation } = useDeleteChemicalData();

  const chemicalValues: IChemicalRecord[] = useMemo(() => {
    if (!chemicalData) return [];
    return chemicalData.map((item: any) => ({
      ...item,
      _id: item._id,
      chemicalName: item?.chemicalName || "",
      chemicalType: `${item.chemicalType}` || DEFAULT_CHEMICAL,
      amount: item.amount || "",
      isTransportationDetailsAvailable: Boolean(
        item?.isTransportationDetailsAvailable ||
          item?.commercialChemicalId?.isTransportationDetailsAvailable
      ),
      transportationTypes: item.transportationTypes || [],
      unit: item.unit || "kg"
    }));
  }, [chemicalData]);

  // 1. Initialize Form
  const { control, reset, getValues, trigger, setValue } = useForm<IChemicalForm>({
    defaultValues: { chemicals: chemicalValues },
    mode: "onChange"
  });

  const { toggleTransportation } = useChemicalTransportToggle({
    setValue,
    type,
    supplierDataHandle
  });

  const findChemicalIndexById = useCallback(
    (recordId: string) =>
      getValues("chemicals").findIndex(c => String(c._id) === String(recordId)),
    [getValues]
  );

  const chemicalRecordsLength = useMemo(() => {
    return chemicalData ? chemicalData.length : 0;
  }, [chemicalData]);

  // Initialize Field Array
  const { fields, remove } = useFieldArray({
    control,
    name: "chemicals"
  });

  // Sync backend data and show empty form when accordion opens
  useEffect(() => {
    if (accordionEnabled) {
      const existingRecords = chemicalData || [];
      const currentChemicals = getValues("chemicals") || [];
      const expandedId = expandedRecordIdRef.current;

      const emptyForm = {
        chemicalName: "",
        amount: "",
        chemicalType: chemicalDataType?.[0]?.value || DEFAULT_CHEMICAL,
        isTransportationDetailsAvailable: false,
        transportationTypes: [],
        unit: "kg"
      };

      const mappedRecords = existingRecords.map((item: any) => {
        const itemId = String(item._id);
        const currentRecord = currentChemicals.find(c => String(c._id) === itemId);
        const mappedFlag = Boolean(
          item?.isTransportationDetailsAvailable ||
            item?.commercialChemicalId?.isTransportationDetailsAvailable
        );
        const preserveEnabled =
          expandedId !== null && itemId === String(expandedId) && currentRecord?.isTransportationDetailsAvailable;

        return {
          _id: item._id,
          chemicalName: item.chemicalName || "",
          chemicalType: item.chemicalType || DEFAULT_CHEMICAL,
          amount: item.amount || "",
          isTransportationDetailsAvailable: mappedFlag || Boolean(preserveEnabled),
          transportationTypes: item.transportationTypes || [],
          unit: item.unit || "kg"
        };
      });

      // Always show existing records plus one empty form for new entry
      if (mappedRecords.length > 0) {
        reset({ chemicals: [...mappedRecords, emptyForm] });
      } else {
        reset({ chemicals: [emptyForm] });
      }
    }
  }, [chemicalData, accordionEnabled, reset, getValues]);

  const handleClick = () => {
    // Validate if accordion can be opened (e.g., check if process is saved)
    if (handleOpenValidation && !handleOpenValidation()) {
      return;
    }
    setAccordionEnabled(prev => !prev);
  };

  // Update parent accordion status after local state changes
  useEffect(() => {
    currentAccordionStatus(accordionEnabled, AccordionNames.CHEMICALS);
  }, [accordionEnabled, currentAccordionStatus]);

  const handleSaveChemical = async (
    index: number
  ): Promise<ChemicalSaveOutcome | undefined> => {
    const isValid = await trigger(`chemicals.${index}`);
    if (!isValid) {
      errorView("Please fill all required fields correctly");
      return;
    }
    const recordData = getValues(`chemicals.${index}`);
    // Validation
    if (!recordData.amount || parseFloat(recordData.amount) <= 0) {
      errorView("Amount must be greater than 0");
      return;
    }
    if (!recordData.chemicalName) {
      errorView("Chemical chemicalName is required!");
      return;
    }
    const createChemicalPayload: any = {
      chemicalName: recordData.chemicalName,
      chemicalType: Number(recordData.chemicalType || 1),
      amount: String(recordData.amount)
    };
    const transportData: ITransportType[] = recordData.transportationTypes as ITransportType[];
    try {
      if (recordData._id) {
        // Update existing chemical
        const updateChemicalPayload: any = {
          chemicalName: recordData.chemicalName,
          chemicalType: Number(recordData.chemicalType || 1),
          amount: String(recordData.amount),
          isTransportationDetailsAvailable: recordData.isTransportationDetailsAvailable || false,
          transportationTypes: recordData.transportationTypes || []
        };
        // Include transportation data if available
        if (recordData.isTransportationDetailsAvailable) {
          updateChemicalPayload.isTransportationDetailsAvailable = true;
          // Transform transportationTypes to match backend schema
          if (transportData && transportData.length > 0) {
            updateChemicalPayload.transportationTypes = transportData
              .filter(
                (transport: ITransportType) =>
                  transport.transportType &&
                  transport.transportType.toString().trim() !== "" &&
                  transport.distance !== null &&
                  transport.distance !== undefined
              )
              .map((transport: ITransportType) => ({
                transportType: String(transport.transportType),
                distance: parseFloat(String(transport.distance))
              }));
          }
        }
        const updatePayload = {
          chemicalId: recordData._id as string,
          type,
          supplierDataHandle,
          chemicalData: updateChemicalPayload
        };
        return new Promise(resolve => {
          putChemicalData(updatePayload, {
            onSuccess: response => {
              successMessage("Chemical updated successfully");
              // Update form with fresh backend data immediately
              if (response?.data) {
                const updatedData = response.data;
                setValue(`chemicals.${index}._id`, updatedData._id);
                setValue(
                  `chemicals.${index}.chemicalName`,
                  updatedData.commercialChemicalId?.chemicalName || updatedData.chemicalName
                );
                setValue(
                  `chemicals.${index}.chemicalType`,
                  updatedData.commercialChemicalId?.chemicalMixture?.[0]?.chemicalType ||
                    updatedData.chemicalType
                );
                setValue(`chemicals.${index}.amount`, String(updatedData.amount));
                setValue(
                  `chemicals.${index}.isTransportationDetailsAvailable`,
                  updatedData.commercialChemicalId?.isTransportationDetailsAvailable || false
                );
              }
              if (onChemicalSave) onChemicalSave(processId);
              refetchChemical();
              resolve("updated");
            },
            onError: error => {
              errorView(error?.msg || "Failed to update chemical");
              resolve(undefined);
            }
          });
        });
      }

      // Create new chemical
      const createPayload = {
        processingId: processId,
        type,
        supplierDataHandle,
        chemicalData: createChemicalPayload
      };
      return new Promise(resolve => {
        postChemicalData(createPayload, {
          onSuccess: response => {
            successMessage("Chemical created successfully");
            if (response.data?._id) {
              setValue(`chemicals.${index}._id`, response.data._id);
              setExpandedRecordId(String(response.data._id));
              if (!lockStatus) {
                setTransportPrompt({ index, recordId: response.data._id });
              }
            }
            if (onChemicalSave) onChemicalSave(processId);
            refetchChemical();
            resolve("created");
          },
          onError: error => {
            errorView(error?.msg || "Failed to create chemical");
            resolve(undefined);
          }
        });
      });
    } catch (error) {
      console.error("Failed to save chemical", error);
      errorView("An error occurred while saving");
      return undefined;
    }
  };

  const handleDeclineTransport = useCallback((): void => {
    if (!transportPrompt) return;

    const { recordId } = transportPrompt;
    const index = findChemicalIndexById(recordId);
    if (index < 0) {
      setTransportPrompt(null);
      setExpandedRecordId(null);
      return;
    }

    const record = getValues(`chemicals.${index}`);
    const fieldPrefix = `chemicals.${index}`;

    toggleTransportation({
      enabled: false,
      recordId,
      fieldPrefix,
      chemicalName: record.chemicalName,
      chemicalType: record.chemicalType,
      amount: record.amount
    });
    setTransportPrompt(null);
    setExpandedRecordId(null);
  }, [transportPrompt, findChemicalIndexById, getValues, toggleTransportation]);

  const handleConfirmAddTransport = useCallback((): void => {
    if (!transportPrompt) return;

    confirmingTransportRef.current = true;

    const { recordId } = transportPrompt;
    const index = findChemicalIndexById(recordId);
    if (index < 0) {
      errorView("Chemical record not found");
      setTransportPrompt(null);
      return;
    }

    const record = getValues(`chemicals.${index}`);
    const fieldPrefix = `chemicals.${index}`;

    toggleTransportation({
      enabled: true,
      recordId,
      fieldPrefix,
      chemicalName: record.chemicalName,
      chemicalType: record.chemicalType,
      amount: record.amount
    });
    setExpandedRecordId(String(recordId));
    setTransportPrompt(null);
  }, [transportPrompt, findChemicalIndexById, getValues, toggleTransportation]);

  const handleDeleteClick = (index: number, id?: string | number): void => {
    const chemicalName =
      chemicalData[index]?.commercialChemicalId?.chemicalName ||
      chemicalData[index]?.name ||
      "this chemical";
    setChemicalToDelete({ index, id, name: chemicalName });
    setDeleteModalOpen(true);
  };

  const handleRemoveChemicalRecord = async (index: number, id?: string | number): Promise<void> => {
    if (id) {
      // Delete from backend
      const deletePayload = {
        type,
        supplierDataHandle,
        chemicalData: {
          chemicalId: id as string,
          processingId: processId
        }
      };

      deleteChemicalDataMutation(deletePayload, {
        onSuccess: () => {
          successMessage("Chemical deleted successfully");
          if (onChemicalSave) onChemicalSave(processId);
          refetchChemical();
          remove(index);
        },
        onError: error => {
          errorView(error?.msg || "Failed to delete chemical");
        }
      });
    } else {
      remove(index);
    }
  };

  return (
    <Card
      className={`tw-border-border  tw-pt-0 tw-pb-0 tw-mt-1`}
    >
      <CardHeader
        className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
        onClick={handleClick}
      >
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Beaker className="tw-h-4 tw-w-4 tw-text-primary" />
            <span className="tw-text-sm tw-font-semibold tw-text-foreground">Chemicals</span>
            {chemicalRecordsLength > 0 && (
              <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                {chemicalRecordsLength}
              </Badge>
            )}
          </div>
          {accordionEnabled ? (
            <ChevronUp className="tw-h-4 tw-w-4" />
          ) : (
            <ChevronDown className="tw-h-4 tw-w-4" />
          )}
        </div>
      </CardHeader>

      {accordionEnabled && (
        <CardContent className="tw-py-3.5 tw-px-3.5 tw-space-y-5">
          <div className="tw-space-y-3">
            {/* Render the Array */}
            {fields.map((field, index) => {
              const isSavedRecord = !!(field as any)._id;
              // Use stable key: _id for saved records, field.id for new records
              // This prevents component remounting and preserves isEditing state on refetch
              const stableKey = (field as any)._id || field.id;
              const recordId = (field as any)._id;
              return (
                <ChemicalRecord
                  key={stableKey}
                  index={index}
                  fieldPrefix={`chemicals.${index}`}
                  isSavedRecord={isSavedRecord}
                  recordId={recordId}
                  control={control}
                  setValue={setValue}
                  lockStatus={lockStatus}
                  chemicalTypeOptions={chemicalDataType}
                  TransportTypeList={TransportTypeList}
                  handleRemoveChemicalRecord={handleDeleteClick}
                  handleSaveChemical={handleSaveChemical}
                  processId={processId}
                  onChemicalSave={onChemicalSave}
                  refetchChemical={refetchChemical}
                  type={type}
                  supplierDataHandle={supplierDataHandle}
                  keepExpanded={
                    recordId != null && String(recordId) === String(expandedRecordId)
                  }
                  onCollapse={() => {
                    if (recordId != null && String(recordId) === String(expandedRecordId)) {
                      setExpandedRecordId(null);
                    }
                  }}
                />
              );
            })}
          </div>
        </CardContent>
      )}

      <ChemicalTransportPromptDialog
        open={transportPrompt !== null}
        onOpenChange={open => {
          if (!open) {
            if (!confirmingTransportRef.current) {
              handleDeclineTransport();
            }
            confirmingTransportRef.current = false;
          }
        }}
        onConfirm={handleConfirmAddTransport}
        onDecline={handleDeclineTransport}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && chemicalToDelete && (
        <ConfirmModal
          viewModal={deleteModalOpen}
          closeModal={() => {
            setDeleteModalOpen(false);
            setChemicalToDelete(null);
          }}
          cancel={() => {
            setDeleteModalOpen(false);
            setChemicalToDelete(null);
          }}
          approve={() => {
            handleRemoveChemicalRecord(chemicalToDelete.index, chemicalToDelete.id);
            setDeleteModalOpen(false);
            setChemicalToDelete(null);
          }}
          title={`Delete Chemical`}
          subTitle={`Are you sure you want to remove "${chemicalToDelete.name}"? This action cannot be undone.`}
          style={{ zIndex: 10001 }}
          className="tw-delete-confirmation-modal"
          mountNode={document.body}
        />
      )}
    </Card>
  );
};
